import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { ActivityLog } from '../lib/database.types'

export const ACTIVITY_LOG_KEY = ['activity_log']

export type ActivityEntity =
  | 'sale'
  | 'print'
  | 'build'
  | 'maintenance_event'
  | 'expense'
  | 'consignee_handoff'
  | 'consignee_settlement'

export type ActivityAction =
  | 'created'
  | 'updated'
  | 'deleted'
  | 'refunded'
  | 'unrefunded'
  | 'completed'
  | 'abandoned'
  | 'settled'

export interface LogActivityInput {
  user_id: string  // workspace owner (data being logged about)
  actor_user_id: string  // who's doing it; same as user_id for owner-only ops today
  entity_type: ActivityEntity
  entity_id: string | null
  action: ActivityAction
  metadata?: Record<string, unknown>
}

/**
 * Fire-and-forget write to the activity log. Failures are logged to console
 * but never thrown — a failed log row should never roll back the real action.
 */
export async function logActivity(input: LogActivityInput): Promise<void> {
  const { error } = await supabase.from('activity_log').insert({
    user_id: input.user_id,
    actor_user_id: input.actor_user_id,
    entity_type: input.entity_type,
    entity_id: input.entity_id,
    action: input.action,
    metadata: input.metadata ?? {},
  })
  if (error) {
    console.error('activity_log insert failed:', error)
  }
}

export interface UseActivityLogOptions {
  /** Cap at this many rows. Default 100. */
  limit?: number
  /** Filter to only one entity type. */
  entityType?: ActivityEntity
}

/**
 * Reads the recent activity log. The owner sees everything in their
 * workspace (per RLS); consignees see only rows where actor_user_id
 * matches them (also per RLS, after Part B's consignee policies land).
 */
export function useActivityLog(opts: UseActivityLogOptions = {}) {
  const { limit = 100, entityType } = opts
  return useQuery({
    queryKey: [...ACTIVITY_LOG_KEY, { limit, entityType }],
    queryFn: async (): Promise<ActivityLog[]> => {
      let q = supabase
        .from('activity_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit)
      if (entityType) q = q.eq('entity_type', entityType)
      const { data, error } = await q
      if (error) throw error
      return data ?? []
    },
  })
}

/** Convenience: invalidate the activity log cache after a logged action. */
export function useInvalidateActivityLog() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ACTIVITY_LOG_KEY })
}
